/**
 * Plans v2 (owner, 15–16.10.2026): what a plan lets a user create ON THIS
 * MACHINE. The server is the source of truth (`plan_configs.local_*_limit`,
 * served by `/auth/usage` and the signed plan claim); this file only holds
 * the vocabulary and the fallback used before the server has answered.
 *
 * Free is a preview: a few of everything, the Personal workspace only, no
 * cloud. Basic, Pro, Team and Enterprise are unlimited locally.
 */

/** Every capped kind, its server key, and the plan that lifts the cap. */
export const LOCAL_KINDS = Object.freeze({
  boards: 'local_boards',
  tasks: 'local_tasks',
  documents: 'local_documents',
  collections: 'local_collections',
  requests: 'local_requests',
  environments: 'local_environments',
  dbConnections: 'local_db_connections',
  hosts: 'local_hosts',
  calendarEvents: 'local_calendar_events',
  // Local API workspaces BESIDES Personal: Free has none.
  workspaces: 'local_workspaces',
});

/**
 * Free's caps when the server has not answered yet. Mirrors migration
 * 20261015120000; the server's numbers replace these as soon as they arrive,
 * so a change in the admin's plan configs wins without a release.
 */
export const FREE_LOCAL_LIMITS = Object.freeze({
  boards: 1,
  tasks: 25,
  documents: 5,
  collections: 2,
  requests: 20,
  environments: 1,
  dbConnections: 1,
  hosts: 1,
  calendarEvents: 20,
  workspaces: 0,
});

/** Plans with no local cap at all. */
export const UNLIMITED_LOCAL_PLANS = Object.freeze(['basic', 'pro', 'team', 'enterprise']);

/** The cheapest plan that lifts every local cap. */
export const LOCAL_UPGRADE_PLAN = 'basic';

const UNLIMITED = Object.freeze(Object.fromEntries(Object.keys(LOCAL_KINDS).map((k) => [k, null])));

/** `{ kind: number | null }` (null = unlimited) for a plan name, before the server answers. */
export function defaultLocalLimits(plan) {
  return UNLIMITED_LOCAL_PLANS.includes(String(plan || '').toLowerCase()) ? { ...UNLIMITED } : { ...FREE_LOCAL_LIMITS };
}

/**
 * The server's limits map (`/auth/usage` `limits`, or a plan claim's `limits`:
 * `{ local_boards: 1, …, local_tasks: null }`) → `{ boards: 1, …, tasks: null }`.
 * Kinds the server did not send keep `fallback`'s value; anything that is not
 * a non-negative integer or null is ignored.
 */
export function localLimitsFrom(serverLimits, fallback) {
  const out = { ...fallback };
  if (!serverLimits || typeof serverLimits !== 'object') return out;
  for (const [kind, key] of Object.entries(LOCAL_KINDS)) {
    if (!(key in serverLimits)) continue;
    const v = serverLimits[key];
    if (v === null || (Number.isInteger(v) && v >= 0)) out[kind] = v;
  }
  return out;
}

/**
 * Can `adding` more `kind` (default 1: an import or a duplicate adds several)
 * be created when `used` already exist?
 * `{ ok: true }` or `{ ok: false, kind, limit, used, adding, requiredPlan }`.
 * Existing items over the cap are never touched: only creating is refused.
 */
export function checkLimit(limits, kind, used, adding = 1) {
  const limit = limits?.[kind];
  if (limit === null || limit === undefined) return { ok: true };
  const count = Math.max(0, Number(used) || 0);
  const more = Math.max(1, Number(adding) || 1);
  if (count + more <= limit) return { ok: true };
  return { ok: false, kind, limit, used: count, adding: more, requiredPlan: LOCAL_UPGRADE_PLAN };
}
